import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../utils/api';
import useUserStore from '../store/userStore';

// PHASE 3 — single shared source of truth for durable AI-log background jobs. A module-level store +
// ONE polling loop are shared across every consumer (Home, AI Log), so mounting the hook in two places
// never starts two loops or hammers the API. Backend/Mongo job state is authoritative; sessionStorage
// is never used here. Polling runs only while a job is queued/processing, and refreshes on app-resume.
const ACTIVE = new Set(['queued', 'processing']);
const POLL_MS = 5000;

const store = {
  jobs: [],
  loading: false,
  disabled: false,          // backend flag OFF (403) -> render nothing, legacy flow intact
  listeners: new Set(),
  timer: null,
  inflight: false,
  again: false,             // a refresh was requested while a read was in flight -> read once more
  ownerId: null,            // user_id whose jobs this store holds (null = signed out)
  epoch: 0,                 // +1 on every account change; responses from an older epoch are ignored
  accepted: new Map(),      // job_id -> { job, addedAtSeq }: jobs the backend accepted for THIS account
  fetchSeq: 0,              // +1 each time a list request actually starts (orders lists vs acceptances)
};

function emit() { store.listeners.forEach((fn) => fn()); }

function currentUserId() {
  const u = useUserStore.getState().user;
  return (u && u.user_id) || null;
}

// Account isolation: this module-level store only ever holds the CURRENT account's jobs. On logout or an
// account switch everything is dropped and polling stops; a request still in flight is ignored on arrival.
// Jobs themselves live on the server per user_id — signing back in simply fetches that account's own list.
function syncOwner() {
  const id = currentUserId();
  if (id === store.ownerId) return;
  if (store.timer) { clearTimeout(store.timer); store.timer = null; }
  store.ownerId = id;
  store.epoch += 1;
  store.jobs = [];
  store.accepted = new Map();
  store.loading = false;
  store.disabled = false;
  store.inflight = false;
  store.again = false;
  emit();
}
store.ownerId = currentUserId();
useUserStore.subscribe(syncOwner);

async function fetchOnce() {
  syncOwner();
  if (store.inflight) { store.again = true; return; }
  const epoch = store.epoch;
  const seq = ++store.fetchSeq;
  store.inflight = true;
  store.again = false;
  try {
    // A safe read: the shared api.js lifecycle gives it a finite timeout and one transport retry,
    // so `inflight` can never stick and stall polling.
    const { data } = await api.get('/ai-log/jobs');
    if (epoch !== store.epoch) return;          // started under a previous account: discard
    store.jobs = (data && data.jobs) || [];
    store.disabled = false;
    // Cleanup rule: forget an accepted job only when a list that STARTED AFTER its acceptance succeeds
    // without it (committed / discarded / expired). A list started before acceptance can never remove it.
    const listed = new Set(store.jobs.map((j) => j.job_id));
    store.accepted.forEach((entry, id) => { if (!listed.has(id) && seq > entry.addedAtSeq) store.accepted.delete(id); });
  } catch (e) {
    if (epoch !== store.epoch) return;
    const st = e && e.response && e.response.status;
    if (st === 401 || st === 402 || st === 403) {
      // Definitive access/auth change (gated off, logged out, or Pro-required): clear cached jobs so
      // one account's cards can't survive a session/account transition. Never triggers the paywall here
      // (the section only fetches when availability says jobs_available).
      store.disabled = true; store.jobs = []; store.accepted = new Map();
    }
    // ordinary transient network error: keep last-known jobs (don't wipe on a blip)
  } finally {
    if (epoch === store.epoch) {                // otherwise the account reset already owns the store
      store.inflight = false;
      store.loading = false;
      emit();
      if (store.again) { store.again = false; fetchOnce(); } else { reschedule(); }
    }
  }
}

// Placeholders = accepted jobs the server list doesn't show yet (newest first). The server copy always wins.
function placeholders() {
  const listed = new Set(store.jobs.map((j) => j.job_id));
  const out = [];
  store.accepted.forEach((entry, id) => { if (!listed.has(id)) out.unshift(entry.job); });
  return out;
}

// An unconfirmed placeholder keeps the EXISTING 5s poll running until the server lists the job.
function hasActive() { return store.jobs.some((j) => ACTIVE.has(j.status)) || placeholders().length > 0; }

// Register a job the backend ACCEPTED for the signed-in account that submitted it. Returns false and does
// nothing (no request) for an invalid job_id, a missing owner, or an account change since submission.
function addAccepted(ownerAtSubmit, job) {
  syncOwner();
  const id = job && typeof job.job_id === 'string' ? job.job_id.trim() : '';
  if (!id || !ownerAtSubmit || ownerAtSubmit !== store.ownerId) return false;
  if (!store.accepted.has(id)) {
    store.accepted.set(id, { addedAtSeq: store.fetchSeq, job: {
      job_id: id, status: 'queued', input_kind: job.input_kind || null,
      submitted_local_date: job.submitted_local_date || null,
      suggested_meal_type: job.suggested_meal_type || null } });
  }
  emit();
  fetchOnce();              // existing coalescing: an in-flight read is followed by exactly one newer read
  return true;
}

function reschedule() {
  if (store.timer) { clearTimeout(store.timer); store.timer = null; }
  if (store.disabled) return;                 // feature off -> no polling
  if (store.listeners.size === 0) return;     // nothing mounted
  if (!hasActive()) return;                    // no queued/processing -> stop continuous polling
  store.timer = setTimeout(fetchOnce, POLL_MS);
}

export function useAiLogJobs(enabled = true) {
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;                       // gated OFF -> never touch /jobs (no unsolicited 402 paywall)
    store.listeners.add(rerender);
    if (store.jobs.length === 0 && !store.inflight) store.loading = true;
    fetchOnce();                                // fetch on mount
    const onResume = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      store.listeners.delete(rerender);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      if (store.listeners.size === 0 && store.timer) { clearTimeout(store.timer); store.timer = null; }
    };
  }, [rerender, enabled]);

  const refresh = useCallback(() => fetchOnce(), []);
  const retry = useCallback(async (jobId) => {
    await api.post(`/ai-log/jobs/${jobId}/retry`);
    await fetchOnce();                          // resume polling once it is queued again
  }, []);
  const discard = useCallback(async (jobId) => {
    await api.post(`/ai-log/jobs/${jobId}/discard`);
    await fetchOnce();
  }, []);

  return { jobs: placeholders().concat(store.jobs), serverJobs: store.jobs,
           acceptedIds: new Set(store.accepted.keys()),
           loading: store.loading, disabled: store.disabled, refresh, retry, discard, addAccepted };
}

export default useAiLogJobs;
